import React from 'react';
/**
 * QuestionCardClean — Card wrapper for question display.
 *
 * Renders the question text with LaTeX support, optional stimulus
 * visual aids, and the answer area (children). Uses the question type
 * registry to determine which types have built-in visuals.
 *
 * Co-located in practice/ domain (CL-1 s63).
 *
 * Note: Entrance/exit animations are handled by PracticeSession's
 * AnimatePresence wrapper — this component is intentionally static.
 */
import { type Question, type QuestionType } from '../../content/types';
import { QUESTION_TYPE_REGISTRY } from '../../content/questions/questionTypeRegistry';
import { VisualAidFactory } from '../../visuals';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
// Derive from registry — no fragile hardcoded list to maintain
const interactiveTypesWithBuiltInVisuals = new Set(Object.entries(QUESTION_TYPE_REGISTRY).filter(([_, config]) => config.hasBuiltInVisuals).map(([type]) => type));
interface QuestionCardCleanProps {
  question: Question;
  children: React.ReactNode;
  isFluency?: boolean;
  moduleColor?: string;
}
export function QuestionCardClean({
  question,
  children,
  isFluency = false,
  moduleColor
}: QuestionCardCleanProps) {
  const shouldShowStimulusAids = !interactiveTypesWithBuiltInVisuals.has(question.questionType || '');
  const stimulusVisuals = shouldShowStimulusAids ? question.stimulusAids ?? [] : [];
  // Show module color strip when moduleColor is provided and NOT in fluency mode
  const showModuleStrip = !isFluency && moduleColor;
  return <div className="w-full max-w-3xl mx-auto" data-id="element-8142">
      <div className={`bg-white rounded-2xl md:rounded-3xl border-2 ${isFluency ? 'border-amber-300' : 'border-slate-200'} shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden`} data-id="element-8143">
        {/* Module color accent strip OR Fluency accent strip */}
        {isFluency && <div className="h-1 w-full bg-amber-400" data-id="element-8144" />}
        {showModuleStrip && <div className="h-1 w-full" style={{
        backgroundColor: moduleColor
      }} data-id="element-8145" />}

        {/* Question Text */}
        <div className="p-5 sm:p-6 md:p-8 border-b-2 border-slate-100 bg-slate-50" data-id="element-8146">
          <div className="text-fluid-xl md:text-fluid-2xl font-black text-slate-800 leading-relaxed whitespace-pre-line tracking-tight" data-id="element-8147">
            <LatexRenderer content={question.question} data-id="element-8148" />
          </div>
        </div>

        {/* Stimulus Aids Zone — compact visual only */}
        {stimulusVisuals.length > 0 && <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-b border-slate-100 space-y-4 md:space-y-6" data-id="element-8149">
            {stimulusVisuals.map((aid, index) => <VisualAidFactory key={index} visualAid={aid} isCorrect={undefined} mode="stimulus" data-id="element-8150" />)}
          </div>}

        {/* Answer Area */}
        <div className="p-5 sm:p-6 md:p-8" data-id="element-8151">{children}</div>
      </div>
    </div>;
}